import {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Progress,
    Button,
    Row,
    Col,
    Spinner,
    Alert,
} from 'reactstrap';
import imageCompression from 'browser-image-compression';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUpload, faTrash, faFileAlt, faTimes, faPaperclip,
} from '@fortawesome/free-solid-svg-icons';
import { ConfirmDelete } from '../Modal';
import { toasty, SmallButton } from './FormElements';

const MAX_FILE_SIZE_MB = 50;
const defaultFiles = [];
const defaultButtonProps = {};

const isImageFile = (file) => file.type.startsWith('image/');

/** Helper Function: Compress image file */
const compressImage = async (file, onProgress) => {
    const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        onProgress,
    };

    try {
        const compressedFile = await imageCompression(file, options);
        // Preserve the original filename
        return new File([compressedFile], file.name, {
            type: compressedFile.type,
            lastModified: Date.now(),
        });
    } catch {
        return file;
    }
};

/** Helper Function: Truncate File Name */
const truncateFileName = (filename) => {
    const ext = filename.split('.').pop();
    return filename.length > 17 ? `${filename.substring(0, 14)}...${ext}` : filename;
};

const FileUpload = ({
    id,
    url,
    accept,
    files = defaultFiles,
    onUploadComplete,
    onQueueComplete,
    onQueueStarted,
    onDownload,
    onDelete,
    type = 'default',
    disabled = false,
    buttonText = '',
    buttonProps = defaultButtonProps,
    stacked = false,
    fileIcon = faFileAlt,
    buttonIcon = faPaperclip,
}) => {
    const [uploadedFiles, setUploadedFiles] = useState(files);
    const [uploadQueue, setUploadQueue] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [totalProgress, setTotalProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const [compressionQueue, setCompressionQueue] = useState([]);
    const isCompressing = compressionQueue.length > 0 && compressionQueue.some((f) => f.isCompressing);
    const isUploading = uploadQueue.length > 0 || isCompressing;

    // Track files that have already been uploaded
    const uploadedIdsRef = useRef(new Set());
    const batchUploadsRef = useRef([]);
    const prevQueueLenRef = useRef(0);

    /** Keep `uploadedFiles` updated with parent prop */
    useEffect(() => {
        if (files && JSON.stringify(files) !== JSON.stringify(uploadedFiles)) {
            setUploadedFiles(files);
        }
    }, [files, uploadedFiles]);


    /** Handle File Drop */
    const onDrop = useCallback(async (acceptedFiles) => {
        setIsDragging(false);
        setErrorMessage(null);

        const existingFileNames = new Set(uploadedFiles.map((f) => f.name.toLowerCase()));
        const existingUploadNames = new Set(uploadQueue.map((f) => f.name.toLowerCase()));

        const duplicateFiles = [];
        const oversizedFiles = [];
        const validFiles = [];
        const filesToCompress = [];

        acceptedFiles.forEach((file) => {
            const fileName = file.name.toLowerCase();
            const isDuplicate = existingFileNames.has(fileName) || existingUploadNames.has(fileName);
            const isTooLarge = file.size > MAX_FILE_SIZE_MB * 1024 * 1024;

            if (isDuplicate) {
                duplicateFiles.push(file.name);
            } else if (isTooLarge) {
                oversizedFiles.push(file.name);
            } else {
                const fileItem = {
                    id: file.name + file.size,
                    file,
                    name: truncateFileName(file.name),
                    size: `${(file.size / 1024).toFixed(1)} KB`,
                    progress: 0,
                    uploaded: false,
                    error: null,
                    isCompressing: isImageFile(file),
                    compressionProgress: 0,
                };
                validFiles.push(fileItem);

                if (isImageFile(file)) {
                    filesToCompress.push(fileItem);
                }
            }
        });

        // Keep error message until user dismisses it
        const errorMessages = [];

        if (duplicateFiles.length > 0) {
            errorMessages.push(`Duplicate Files: ${duplicateFiles.join(', ')}`);
        }
        if (oversizedFiles.length > 0) {
            errorMessages.push(`Oversized Files (> ${MAX_FILE_SIZE_MB}MB): ${oversizedFiles.join(', ')}`);
        }

        if (errorMessages.length > 0) {
            setErrorMessage(errorMessages.join(' | '));
        }

        if (validFiles.length > 0) {
            if (uploadQueue.length === 0) {
                batchUploadsRef.current = [];
                if (typeof onQueueStarted === 'function') {
                    onQueueStarted();
                }
                setTotalProgress(0);
            }

            // Separate images from non-images
            const nonImageFiles = validFiles.filter((f) => !isImageFile(f.file));

            // Only add images to compression queue
            setCompressionQueue((prev) => [...prev, ...filesToCompress]);

            // Add non-images directly to upload queue
            if (nonImageFiles.length > 0) {
                setUploadQueue((prev) => [...prev, ...nonImageFiles]);
            }

            // Compress images if any
            if (filesToCompress.length > 0) {
                const compressionPromises = filesToCompress.map(async (fileItem) => {
                    try {
                        const compressedFile = await compressImage(
                            fileItem.file,
                            (progress) => {
                                setCompressionQueue((prev) =>
                                    prev.map((f) =>
                                        f.id === fileItem.id
                                            ? { ...f, compressionProgress: Math.round(progress) }
                                            : f,
                                    ),
                                );
                            },
                        );

                        // Update file item with compressed file
                        fileItem.file = compressedFile;
                        fileItem.size = `${(compressedFile.size / 1024).toFixed(1)} KB`;
                        fileItem.isCompressing = false;

                        return fileItem;
                    } catch (error) {
                        console.error('Error during compression:', error);
                        // Return original file without compression
                        fileItem.isCompressing = false;
                        return fileItem;
                    }
                });

                // Wait for all compressions to complete
                const compressedFiles = await Promise.all(compressionPromises);

                // Clear compression queue and add all to upload queue at once
                setCompressionQueue([]);
                setUploadQueue((prev) => [...prev, ...compressedFiles]);
            }
        }
    }, [uploadedFiles, uploadQueue, onQueueStarted]);

    /** Handle Upload Progress */
    const onProgressUpdated = useCallback((e, fileId) => {
        if (e.lengthComputable) {
            setUploadQueue((prevUploads) => {
                const updatedUploads = prevUploads.map((file) =>
                    file.id === fileId ? { ...file, progress: Math.round((e.loaded / e.total) * 100) } : file,
                );

                // Calculate total progress (average of all active uploads)
                const total = updatedUploads.reduce((sum, file) => sum + (file.progress || 0), 0);
                const count = updatedUploads.length || 1;
                setTotalProgress(Math.round(total / count));

                return updatedUploads;
            });
        }
    }, []);

    /** Auto-start Uploads */
    useEffect(() => {
        if (uploadQueue.length === 0) {
            uploadedIdsRef.current.clear();
            return;
        }

        uploadQueue.forEach((upload) => {
            if (!upload.uploaded && !upload.error && !uploadedIdsRef.current.has(upload.id)) {
                uploadedIdsRef.current.add(upload.id);

                const formData = new FormData();
                const truncatedName = truncateFileName(upload.file.name);
                const fileWithTruncatedName = new File([upload.file], truncatedName, {
                    type: upload.file.type,
                });
                formData.append('file', fileWithTruncatedName);

                const xhr = new XMLHttpRequest();
                xhr.open('POST', url, true);
                xhr.upload.onprogress = (e) => onProgressUpdated(e, upload.id);

                const currentTenantId = window.sessionStorage.getItem('currentTenantId');

                if (currentTenantId) {
                    xhr.setRequestHeader('X-CurrentTenantId', currentTenantId);
                }

                const impersonating = window.sessionStorage.getItem('impersonating');

                if (impersonating) {
                    xhr.setRequestHeader('X-Impersonating', impersonating);
                }

                xhr.onload = () => {
                    try {
                        if (xhr.status === 200) {
                            if (!xhr.responseText.trim()) {
                                console.warn('Upload successful but received empty response.');
                                setUploadQueue((prev) => prev.filter((file) => file.id !== upload.id));
                                if (typeof onUploadComplete === 'function') {
                                    onUploadComplete(null);
                                }
                                return;
                            }

                            const uploadedFile = JSON.parse(xhr.responseText);
                            setUploadedFiles((prev) => [...prev, uploadedFile]);
                            batchUploadsRef.current.push(uploadedFile);
                            setUploadQueue((prev) => prev.filter((file) => file.id !== upload.id));
                            if (typeof onUploadComplete === 'function') {
                                onUploadComplete(uploadedFile);
                            }
                        } else {
                            console.error('Upload failed with status:', xhr.status, xhr.statusText);
                        }
                    } catch (error) {
                        console.error('Error parsing upload response:', error.message);
                    }
                };
                xhr.onerror = () => {
                    toasty.error('Could not upload file: ', upload?.file?.name);
                    setUploadQueue((prev) => prev.filter((file) => file.id !== upload.id));
                };
                xhr.send(formData);
            }
        });
    }, [uploadQueue, url, onProgressUpdated, onUploadComplete]);

    useEffect(() => {
        const prev = prevQueueLenRef.current;
        const current = uploadQueue.length;
        if (prev > 0 && current === 0) {
            if (typeof onQueueComplete === 'function') {
                onQueueComplete(batchUploadsRef.current.slice());
            }

            batchUploadsRef.current = [];
            uploadedIdsRef.current.clear();
        }
        prevQueueLenRef.current = current;
    }, [uploadQueue.length, onQueueComplete]);

    const convertAcceptToMime = useCallback((acceptArray) => {
        const mimeMap = {
            '.gif': 'image/gif',
            '.jpg': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.png': 'image/png',
            '.heic': 'image/heic',
            '.webp': 'image/webp',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.xls': 'application/vnd.ms-excel',
            '.pdf': 'application/pdf',
            '.txt': 'text/plain',
            '.doc': 'application/msword',
            '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        };

        return acceptArray?.reduce((acc, ext) => {
            const trimmedExt = ext.trim();
            const mimeType = mimeMap[trimmedExt];
            if (mimeType) acc[mimeType] = [trimmedExt];
            return acc;
        }, {});
    }, []);

    const normalizeAccept = useCallback((accept) => {
        if (!accept) return [];

        if (Array.isArray(accept)) {
            return accept.map((ext) => ext.trim().toLowerCase());
        }

        return accept.split(',').map((ext) => ext.trim().toLowerCase());
    }, []);

    // Convert your accept string before passing it to Dropzone
    const normalizedAccept = normalizeAccept(accept);
    const formattedAccept = convertAcceptToMime(normalizedAccept);

    /** Dropzone Setup */
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onDropRejected: (fileRejections) => {
            const rejectedFiles = fileRejections.map((f) => f.file.name).join(', ');
            setErrorMessage(`Unsupported file type(s): ${rejectedFiles}. Please upload ${accept} only.`);
        },
        accept: formattedAccept,
        onDragEnter: () => setIsDragging(true),
        onDragLeave: () => setIsDragging(false),
        disabled,
    });

    const handleFileDeleteButtonClick = useCallback((fileToDelete) => {
        ConfirmDelete.show({
            title: 'Delete File',
            body: () => `Are you sure you want to delete ${fileToDelete?.name}?`,
            onSubmit: () => {
                if (fileToDelete) {
                    onDelete(fileToDelete);

                    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id));
                    setUploadQueue((prev) => prev.filter((f) => f.id !== fileToDelete.id));

                    uploadedIdsRef.current.delete(fileToDelete.id);

                    setTotalProgress(0);
                }
            },
        });
    }, [onDelete]);

    if (type === 'simple') {
        return (
            <div className="file-upload-container">
                {errorMessage && (
                    <Alert
                        color="danger"
                        toggle={() => setErrorMessage(null)}
                    >
                        {errorMessage}

                        {' '}

                        <Button
                            close
                            onClick={() => setErrorMessage(null)}
                        />
                    </Alert>
                )}

                {compressionQueue.length > 0 && compressionQueue.some((f) => f.isCompressing) && (
                    <div className="mb-2">
                        <Progress
                            value={compressionQueue.reduce((sum, f) => sum + (f.compressionProgress || 0), 0) / compressionQueue.length}
                            color="info"
                            size="sm"
                        />
                        <small>Compressing images...</small>
                    </div>
                )}

                {uploadQueue.length > 0 && (
                    <div className="mb-2">
                        <Progress
                            value={totalProgress}
                            color="primary"
                            size="sm"
                        />
                        <small>{totalProgress}% Uploading...</small>
                    </div>
                )}

                <div className={`d-flex ${stacked ? 'flex-column align-items-stretch' : 'align-items-center'}`}>
                    {/* The dropzone is clickable, so keep it the width of the button it wraps. */}
                    <div {...getRootProps({ className: stacked ? 'align-self-start' : undefined })}>
                        <input {...getInputProps()} />
                        {isUploading ? (
                            <Spinner
                                size="sm"
                                color="primary"
                            />
                        ) : (
                            <SmallButton
                                disabled={disabled}
                                type="button"
                                className="ml-1"
                                {...buttonProps}
                            >
                                <FontAwesomeIcon
                                    icon={buttonIcon}
                                    size="lg"
                                    className="mr-1"
                                />
                                {buttonText}
                            </SmallButton>
                        )}
                    </div>

                    <div className={stacked ? 'w-100' : 'flex-grow-1 ml-3'}>
                        {uploadedFiles.filter(Boolean).map((file) => (
                            <div
                                key={file.id || file.name}
                                className="d-flex align-items-center mb-1"
                            >
                                <Button
                                    color="link"
                                    className="site-link p-0 mr-1"
                                    onClick={() => onDownload(file)}
                                    style={{ fontSize: '0.875rem' }}
                                >
                                    <FontAwesomeIcon
                                        icon={fileIcon}
                                        className="mr-1"
                                        size="sm"
                                    />
                                    {file.name || file.file?.name}
                                </Button>
                                {file.error && (
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="text-danger ml-1"
                                        size="sm"
                                    />
                                )}
                                {!file.error && file.progress !== undefined && (
                                    <div
                                        className="ml-1"
                                        style={{ width: '30px' }}
                                    >
                                        <Progress
                                            value={file.progress}
                                            color={file.progress === 100 ? 'success' : 'info'}
                                            style={{ height: '4px' }}
                                        />
                                    </div>
                                )}
                                {!file.error && file.progress === undefined && (
                                    <Button
                                        color="link"
                                        className="p-0 ml-1 mr-3"
                                        onClick={() => handleFileDeleteButtonClick(file)}
                                        disabled={disabled}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            size="sm"
                                        />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Default type rendering
    return (
        <div className="file-upload-container">
            {/* Show Error Message Below Dropzone */}
            {errorMessage && (
                <Alert
                    color="danger"
                    toggle={() => setErrorMessage(null)}
                >
                    {errorMessage}

                    {' '}

                    <Button
                        close
                        onClick={() => setErrorMessage(null)}
                    />
                </Alert>
            )}

            {/* Compression Progress */}
            {compressionQueue.length > 0 && compressionQueue.some((f) => f.isCompressing) && (
                <div className="mb-3">
                    <Progress
                        value={compressionQueue.reduce((sum, f) => sum + (f.compressionProgress || 0), 0) / compressionQueue.length}
                        color="info"
                        className="global-upload-progress"
                    />
                    <small>Compressing images...</small>
                </div>
            )}

            {/* Global Upload Progress */}
            {uploadQueue.length > 0 && (
                <div className="mb-3">
                    <Progress
                        value={totalProgress}
                        color="primary"
                        className="global-upload-progress"
                    />
                    <small>{totalProgress}% Uploading...</small>
                </div>
            )}

            <Row className="mt-3 align-items-center">
                {/* Upload Dropzone */}
                <Col
                    md={3}
                    xs={12}
                    className="text-center mb-3 mb-md-0"
                >
                    <div
                        {...getRootProps()}
                        id={id}
                        className={`d-flex flex-column align-items-center justify-content-center border border-2 rounded-circle p-4 transition-all cursor-pointer ${isDragging ? 'border-primary' : 'border-secondary border-dashed'
                            }`}
                        style={{
                            width: '160px',
                            height: '160px',
                        }}
                    >
                        <input {...getInputProps()} />
                        {isUploading ? (
                            <Spinner color="primary" />
                        ) : (
                            <>
                                <FontAwesomeIcon
                                    icon={faUpload}
                                    size="2x"
                                    className="mb-2"
                                />
                                <span>Upload Files</span>
                            </>
                        )}
                    </div>
                </Col>

                {/* File List */}
                <Col
                    md={9}
                    xs={12}
                >
                    {uploadedFiles.map((file) => (
                        <div
                            key={file.id || file.name}
                            className="d-flex justify-content-between align-items-center border-bottom py-2"
                        >
                            <div>
                                <Button
                                    color="link"
                                    className="site-link p-0 text-truncate"
                                    onClick={() => onDownload(file)}
                                >
                                    <FontAwesomeIcon
                                        icon={faFileAlt}
                                        className="mr-2"
                                    />
                                    {file.name || file.file?.name}
                                </Button>
                            </div>
                            <div>
                                {file.error ? (
                                    <div className="d-flex align-items-center">
                                        <span className="mr-2">{file.error}</span>
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            className="text-danger"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {file.progress !== undefined ? (
                                            <Progress
                                                value={file.progress}
                                                className="file-progress"
                                                color={file.progress === 100 ? 'success' : 'info'}
                                            />
                                        ) : (
                                            <Button
                                                color="link"
                                                className="site-link p-0"
                                                onClick={() => handleFileDeleteButtonClick(file)}
                                                disabled={disabled}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </Col>
            </Row>
        </div>
    );
};

export default FileUpload;
